import React from 'react'
import { Upload } from 'antd-v4'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { message } from 'antd'
import { abgrToRgba, parseErrorMsg } from '../../../../utilities/helpers'
import { RcCustomRequestOptions } from 'antd-v4/lib/upload/interface'
import { DEFAULT_FILL_COLOR, DEFAULT_LINE_COLOR, PolygonData } from './EcommerceSettingModels'
import { isEqual } from 'lodash'

// NOTE REGARDING COLORS: colors in KML files are in ABGR format: first 2 chars are the opacity (alpha), then blue, green, red.
// We are converting to RGBA format during parsing.
interface Props {
  upload: (polygons: PolygonData[]) => void
}
interface Style {
  fillColor?: string | null
  lineColor?: string | null
}

const KMLUploader = (props: Props) => {
  const readAndParseFile = (data: { file: File }) => {
    if (data.file) {
      const reader = new FileReader()

      reader.onload = (e) => {
        const kmlString = e.target?.result as string
        if (kmlString) {
          try {
            parseKMLToJSON(kmlString)
          } catch (error) {
            message.error(parseErrorMsg(error) || 'Failed to parse KML file')
          }
        }
      }

      reader.onerror = () => {
        message.error('Failed to read file')
      }

      reader.readAsText(data.file)
    } else {
      message.error('File is not valid')
    }
  }

  const parseKMLToJSON = (kmlString: string) => {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(kmlString, 'text/xml')

    if (xmlDoc.getElementsByTagName('parsererror').length) {
      throw new Error('Invalid KML format')
    }

    const stylesMap = extractStyles(xmlDoc)
    const polygonData = extractPolygons(xmlDoc, stylesMap).reverse()

    props.upload(polygonData)
  }

  const extractStyles = (xmlDoc: Document) => {
    const styleMap: { [key: string]: Style } = {}
    const styles = Array.from(xmlDoc.getElementsByTagName('Style'))

    styles.forEach((style) => {
      const id = style.getAttribute('id')
      const lineColor =
        style.getElementsByTagName('LineStyle')[0]?.getElementsByTagName('color')[0]?.textContent ||
        ''
      const fillColor =
        style.getElementsByTagName('PolyStyle')[0]?.getElementsByTagName('color')[0]?.textContent ||
        ''

      if (id) {
        styleMap[id] = {
          fillColor: abgrToRgba(fillColor),
          lineColor: abgrToRgba(lineColor),
        }
      }
    })

    return styleMap
  }

  const extractPolygons = (xmlDoc: Document, stylesMap: { [key: string]: Style }) => {
    const polygons: PolygonData[] = []
    const placemarks = Array.from(xmlDoc.getElementsByTagName('Placemark'))

    placemarks.forEach((placemark) => {
      const name = placemark.getElementsByTagName('name')[0]?.textContent || 'Unnamed Polygon'
      let styleUrl = placemark.getElementsByTagName('styleUrl')[0]?.textContent?.replace('#', '')

      if (styleUrl && !styleUrl.endsWith('-normal')) {
        styleUrl = `${styleUrl}-normal`
      }

      const coordinatesString = placemark
        .getElementsByTagName('coordinates')[0]
        ?.textContent?.trim()

      const coordinates = coordinatesString
        ? coordinatesString.split(/\s+/).map((coord) => {
            const [lon, lat] = coord.split(',').map(Number)
            return [lon, lat]
          })
        : []

      // Placemark without coordinates
      if (coordinates.length === 0) {
        throw new Error('Invalid file. Polygon with no coordinates found in the KML file')
      }

      // Placemark is not a polygon, ex. a point or a line
      // Minimum for a valid polygon: 3 vertices + closing point = 4 coordinates)
      // First and last coordinates should be the same
      if (coordinates.length < 4 || !isEqual(coordinates[0], coordinates[coordinates.length - 1])) {
        throw new Error('Invalid file. Please check if you have valid polygons in the KML file')
      }

      const style = styleUrl ? stylesMap[styleUrl] || {} : {}

      polygons.push({
        name,
        coordinates,
        lineColor: style.lineColor || DEFAULT_LINE_COLOR,
        fillColor: style.fillColor || DEFAULT_FILL_COLOR,
      })
    })

    return polygons
  }

  return (
    <div>
      <Upload
        accept='*.kml'
        showUploadList={false}
        customRequest={(data: RcCustomRequestOptions) => {
          readAndParseFile(data)
        }}
      >
        <BlazeButton titleProps={{ title: 'Upload KML File' }} buttonProps={{}} />
      </Upload>
    </div>
  )
}

export default KMLUploader
